import type { Gender, ActivityLevel, Goal, UserProfile, NutritionTargets, MealCompatibility, AiRecipe } from "../types";

export function calcBMR(gender: Gender, weight_kg: number, height_cm: number, age: number): number {
  const base = 10 * weight_kg + 6.25 * height_cm - 5 * age;
  return gender === "male" ? base + 5 : base - 161;
}

export const ACTIVITY_FACTORS: Record<ActivityLevel, number> = {
  sedentary: 1.2, light: 1.375, moderate: 1.55, active: 1.725,
};

export function calcDailyTargets(profile: UserProfile): NutritionTargets {
  const bmr  = calcBMR(profile.gender, profile.weight_kg, profile.height_cm, profile.age);
  const tdee = bmr * ACTIVITY_FACTORS[profile.activity];
  const cal  = profile.goal === "lose" ? tdee - 500
             : profile.goal === "gain" ? tdee + 300
             : tdee;
  const ratios: Record<Goal, [number, number, number]> = {
    lose:     [0.40, 0.30, 0.30],
    maintain: [0.30, 0.40, 0.30],
    gain:     [0.25, 0.50, 0.25],
  };
  const [pr, cr, fr] = ratios[profile.goal];
  return {
    calories:  Math.round(cal),
    protein_g: Math.round((cal * pr) / 4),
    carbs_g:   Math.round((cal * cr) / 4),
    fat_g:     Math.round((cal * fr) / 9),
  };
}

export function analyzeMealFit(
  recipe: AiRecipe,
  profile: UserProfile,
  targets: NutritionTargets,
  t: (k: string) => string
): MealCompatibility {
  const n = recipe.nutrition;
  if (!n) return { score: 50, label: "moderate", reasons: [] };

  const perMealCal = targets.calories / 3;
  const cal = n.calories ?? 0;
  const protein = n.protein ?? 0;
  const carbs = n.carbs ?? 0;
  const fat = n.fat ?? 0;

  let score = 100;
  const reasons: string[] = [];
  let alternative: string | undefined;

  if (cal > perMealCal * 1.4) {
    score -= 30;
    reasons.push(`${cal} kcal > ${Math.round(perMealCal)} kcal ${t("perMealTarget")}`);
    if (profile.goal === "lose") alternative = t("betterOption") + ": " + (recipe.name.toLowerCase().includes("fried") ? recipe.name.replace(/fried/gi, "grilled") : recipe.name + " (smaller portion)");
  } else if (cal < perMealCal * 0.5 && profile.goal === "gain") {
    score -= 25;
    reasons.push(`${t("perMealTarget")} ${t("goalDesc_gain")}`);
  }

  const perMealProtein = targets.protein_g / 3;
  if (profile.goal === "lose" && protein < perMealProtein * 0.6) {
    score -= 20;
    reasons.push(`${t("protein")}: ${protein}g < ${Math.round(perMealProtein)}g`);
  }
  if (profile.goal === "gain" && protein < perMealProtein * 0.5) {
    score -= 15;
  }

  const perMealCarbs = targets.carbs_g / 3;
  if (profile.goal === "lose" && carbs > perMealCarbs * 1.5) {
    score -= 15;
    reasons.push(`${t("carbs")}: ${carbs}g`);
  }

  const perMealFat = targets.fat_g / 3;
  if (fat > perMealFat * 1.8) {
    score -= 10;
  }

  score = Math.max(0, Math.min(100, score));

  return {
    score,
    label: score >= 70 ? "suitable" : score >= 40 ? "moderate" : "unsuitable",
    reasons,
    alternative,
  };
}
